import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toApiError } from '../../api/client';
import { businessApi, organisationApi, ownerPropertyApi } from '../../api/endpoints';
import { useAuth } from '../../auth/AuthContext';
import type {
  Business,
  BranchSummary,
  MultiBranchStatus,
  OrganisationDashboard,
  PropertyDetail,
} from '../../api/types';
import { ConfirmDialog } from '../../components/Common';
import { EmptyState, ErrorState, InlineError, InlineSuccess, TableSkeleton } from '../../components/States';
import { date, humanise, money, statusLabel } from '../../utils/format';

/**
 * The owner's view of every branch in one organisation.
 *
 * Two independent subscriptions govern what customers can see, and the owner needs both
 * in front of them: the organisation's yearly entitlement at the top, and each branch's
 * own monthly subscription on its card. Where a branch is hidden, the server says why and
 * this page repeats it rather than inferring a reason of its own.
 */
export function OwnerBranchesPage() {
  const navigate = useNavigate();
  const { isOwner } = useAuth();

  const [businesses, setBusinesses] = useState<Business[] | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<OrganisationDashboard | null>(null);
  const [businessesLoading, setBusinessesLoading] = useState(true);
  const [businessesError, setBusinessesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [enabling, setEnabling] = useState(false);

  // An owner may hold several organisations; a branch employee holds exactly one.
  const loadBusinesses = useCallback(async () => {
    setBusinessesLoading(true);
    setBusinessesError(null);

    try {
      const list = await businessApi.list();
      setBusinesses(list);
      setBusinessId((current) => current ?? list[0]?.id ?? null);
    } catch (err) {
      setBusinesses(null);
      setBusinessesError(toApiError(err).message);
    } finally {
      setBusinessesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBusinesses();
  }, [loadBusinesses]);

  const load = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    setError(null);

    try {
      setDashboard(await organisationApi.dashboard(businessId));
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const enableMultiBranch = async () => {
    if (!businessId) return;

    setEnabling(true);
    setError(null);

    try {
      await organisationApi.enableMultiBranch(businessId);
      setSuccess('Multi-branch access is enabled for your organisation.');
      await load();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setEnabling(false);
    }
  };

  if (businessesLoading) return <TableSkeleton rows={4} />;
  if (businessesError) {
    return <ErrorState message={businessesError} onRetry={() => void loadBusinesses()} />;
  }
  if (!businesses || businesses.length === 0) {
    return (
      <EmptyState
        title="No business yet"
        description="Create a business before adding a branch."
      />
    );
  }
  if (loading && !dashboard) return <TableSkeleton rows={4} />;
  if (error && !dashboard) return <ErrorState message={error} onRetry={load} />;
  if (!dashboard) {
    return (
      <EmptyState
        title="No branch information available"
        description="Choose a business to view its branches."
      />
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">{dashboard.organisationName}</h1>
          <p className="sf-muted small mb-0">
            {dashboard.branches.length}{' '}
            {dashboard.branches.length === 1 ? 'branch' : 'branches'}
            {isOwner && dashboard.hiddenFromCustomersCount > 0 && (
              <> · {dashboard.hiddenFromCustomersCount} hidden from customers</>
            )}
          </p>
        </div>

        {isOwner && businesses && businesses.length > 1 && (
          <select
            className="form-select"
            style={{ maxWidth: 260 }}
            value={businessId ?? ''}
            onChange={(e) => setBusinessId(e.target.value)}
            aria-label="Organisation"
          >
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <InlineError message={error} />
      <InlineSuccess message={success} />

      {isOwner ? (
        <MultiBranchPanel
          status={dashboard.multiBranch}
          busy={enabling}
          onEnable={enableMultiBranch}
          onAddBranch={() => navigate(`/owner/branches/new?businessId=${businessId}`)}
        />
      ) : (
        <p className="sf-muted small mb-3">
          Choose an assigned branch to open it. Your access is managed by the business owner.
        </p>
      )}

      {isOwner && dashboard.branchCount > 0 && (
        <div className="row g-2 mb-4 sf-branch-metrics">
          <Metric value={dashboard.activeBranchCount} label="Live now" />
          <Metric value={dashboard.awaitingVerificationCount} label="Awaiting review" />
          <Metric value={dashboard.inGracePeriodCount} label="In grace period" warn />
          <Metric value={dashboard.expiredBranchCount} label="Expired" warn />
          {dashboard.availableBeds !== undefined && dashboard.availableBeds !== null && (
            <Metric value={dashboard.availableBeds} label="Beds free" />
          )}
        </div>
      )}

      {dashboard.branches.length === 0 ? (
        <EmptyState
          title="No branches yet"
          description="Add your first branch to start receiving enquiries."
        />
      ) : (
        dashboard.branches.map((branch) => (
          <BranchCard
            key={branch.id}
            branch={branch}
            businessId={businessId!}
            isOwner={isOwner}
            onChanged={load}
          />
        ))
      )}
    </>
  );
}

function Metric({ value, label, warn }: { value: number; label: string; warn?: boolean }) {
  if (value === 0 && warn) return null;

  return (
    <div className="col-6 col-md-3">
      <div className="sf-card sf-card-pad text-center">
        <div className={`h4 mb-0 ${warn ? 'text-warning' : ''}`}>{value}</div>
        <div className="sf-muted small">{label}</div>
      </div>
    </div>
  );
}

/**
 * The organisation's yearly entitlement.
 *
 * Four states, all decided by the server: free to add, enabled-but-free, needs payment,
 * or switched off. The message is the server's own wording in every case.
 */
function MultiBranchPanel({
  status,
  busy,
  onEnable,
  onAddBranch,
}: {
  status: MultiBranchStatus;
  busy: boolean;
  onEnable: () => void;
  onAddBranch: () => void;
}) {
  const expiring = status.status === 'GracePeriod' || status.status === 'Expired';

  return (
    <div className={`sf-card sf-card-pad mb-3 ${expiring ? 'border-warning' : ''}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <h2 className="h6 mb-1">Organisation subscription</h2>
          <p className="sf-muted small mb-0">{status.message}</p>
        </div>

        {status.status !== 'None' && (
          <span className={`sf-pill sf-pill--${expiring ? 'warn' : 'ok'}`}>
            {status.status === 'GracePeriod' ? 'In grace period' : status.status}
          </span>
        )}
      </div>

      {status.expiryUtc && (
        <p className="small mt-2 mb-0">
          {status.status === 'Expired' ? 'Expired' : 'Valid until'} <strong>{date(status.expiryUtc)}</strong>
          {status.daysRemaining !== undefined && status.daysRemaining > 0 && (
            <> · {status.daysRemaining} days left</>
          )}
          {status.source && status.source !== 'Paid' && (
            <span className="sf-muted"> · granted, not purchased</span>
          )}
        </p>
      )}

      <div className="d-flex flex-wrap gap-2 mt-3">
        {status.canAddBranch && (
          <button type="button" className="btn btn-primary" onClick={onAddBranch}>
            Create another branch
          </button>
        )}

        {/* Only offered when the server says the charge is waived. A paid entitlement is
            activated by a captured payment, never by pressing a button here. */}
        {!status.canAddBranch
          && status.featureEnabled
          && !status.paymentRequired
          && status.status === 'None' && (
            <button type="button" className="btn btn-primary" onClick={onEnable} disabled={busy}>
              {busy ? 'Enabling…' : 'Enable multi-branch'}
            </button>
          )}

        {status.reason === 'EntitlementRequired' && (
          <div className="w-100">
            <p className="small mb-2">
              Yearly subscription: <strong>{money(status.totalAmount)}</strong> ({money(status.amount)} +{' '}
              {status.taxPercentage}% tax), valid {status.durationDays} days, with a {status.gracePeriodDays}-day
              grace period after expiry.
            </p>
            <Link to="/owner/billing" className="btn btn-primary">
              Go to billing
            </Link>
          </div>
        )}

        {status.reason === 'GatewayUnavailable' && (
          <p className="small sf-muted mb-0">
            The configured price is {money(status.totalAmount)} per {status.durationDays} days. Nothing has been
            charged and no payment record has been created.
          </p>
        )}
      </div>
    </div>
  );
}

function BranchCard({
  branch,
  businessId,
  isOwner,
  onChanged,
}: {
  branch: BranchSummary;
  businessId: string;
  isOwner: boolean;
  onChanged: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState<'renew' | 'archive' | 'restore' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<PropertyDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const status = statusLabel(branch.status);

  const toggleDetails = async () => {
    if (detailsOpen) {
      setDetailsOpen(false);
      return;
    }

    setDetailsOpen(true);
    if (detail) return;

    setDetailLoading(true);
    setError(null);
    try {
      setDetail(await ownerPropertyApi.get(branch.id));
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setDetailLoading(false);
    }
  };

  const archive = async () => {
    setBusy(true);
    setError(null);
    try {
      await ownerPropertyApi.archive(branch.id);
      setConfirming(null);
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setError(null);
    try {
      await ownerPropertyApi.restore(branch.id);
      setConfirming(null);
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const deletePermanently = async () => {
    setBusy(true);
    setError(null);
    try {
      await ownerPropertyApi.deletePermanently(branch.id);
      setConfirming(null);
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-card sf-card-pad mb-3 sf-branch-card">
      <InlineError message={error} />
      <div className="sf-branch-card__layout d-flex flex-wrap justify-content-between gap-3">
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <h3 className="h6 mb-0">{branch.name}</h3>
            <span className={`sf-pill sf-pill--${status.variant === 'success' ? 'ok' : 'warn'}`}>{status.text}</span>
            {branch.isArchived && <span className="sf-pill sf-pill--warn">Archived</span>}
            {!branch.visibleToCustomers && <span className="sf-pill sf-pill--warn">Hidden from customers</span>}
          </div>

          <p className="sf-branch-card__address sf-muted small mb-2">
            {branch.address}, {branch.locality}, {branch.city} · {branch.contactPhone}
          </p>

          {branch.hiddenReason && (
            <div className="alert alert-warning py-2 px-3 small mb-2" role="status">
              {branch.hiddenReason}
            </div>
          )}

          <div className="d-flex flex-wrap gap-2 small mb-2">
            {branch.totalBeds !== undefined && branch.totalBeds !== null && (
              <span className="sf-chip">
                {branch.availableBeds ?? 0} of {branch.totalBeds} beds free
              </span>
            )}
            {branch.employeeCount > 0 && (
              <span className="sf-chip">
                {branch.employeeCount} staff {branch.employeeCount === 1 ? 'account' : 'accounts'}
              </span>
            )}
            {branch.newEnquiryCount > 0 && (
              <span className="sf-pill sf-pill--info">{branch.newEnquiryCount} new enquiries</span>
            )}
          </div>

          {branch.subscriptionRequired ? (
            <p className="small mb-0">
              Monthly subscription:{' '}
              <strong>{branch.subscriptionStatus ?? 'Not started'}</strong>
              {branch.subscriptionExpiryUtc && <> · expires {date(branch.subscriptionExpiryUtc)}</>}
              {branch.subscriptionStatus === 'GracePeriod' && branch.graceEndsUtc && (
                <span className="text-warning"> · grace ends {date(branch.graceEndsUtc)}</span>
              )}
            </p>
          ) : (
            <p className="small sf-muted mb-0">
              No payment is required for this category at the moment.
            </p>
          )}
        </div>

        <div className="sf-branch-card__actions d-flex flex-column gap-2">
          <Link to={`/owner/listings/${branch.id}`} className="btn btn-sm btn-primary">
            {isOwner ? 'Manage branch' : 'Open branch'}
          </Link>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => void toggleDetails()}
            aria-expanded={detailsOpen}
          >
            {detailsOpen ? 'Hide facilities & details' : 'See facilities & details'}
          </button>
          {isOwner && (
            <Link to={`/owner/branches/${businessId}/staff`} className="btn btn-sm btn-outline-primary">
              Staff accounts
            </Link>
          )}
          {branch.visibleToCustomers && (
            <Link to={`/listing/${branch.id}`} className="btn btn-sm btn-outline-secondary">
              View public page
            </Link>
          )}
          {isOwner && branch.subscriptionRequired
            && branch.subscriptionStatus !== 'Active' && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setConfirming('renew')}
              >
                Renew
              </button>
            )}
          {!branch.isArchived && (
            <button
              type="button"
              className="btn btn-sm btn-outline-warning"
              onClick={() => setConfirming('archive')}
            >
              Archive branch
            </button>
          )}
          {isOwner && branch.isArchived && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setConfirming('restore')}
            >
              Unarchive branch
            </button>
          )}
          {isOwner && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => setConfirming('delete')}
            >
              Delete permanently
            </button>
          )}
        </div>
      </div>

      {detailsOpen && (
        <div className="border-top mt-3 pt-3">
          {detailLoading ? (
            <p className="sf-muted small mb-0" role="status">Loading branch details…</p>
          ) : detail ? (
            <BranchDetails detail={detail} />
          ) : (
            <p className="sf-muted small mb-0">Branch details could not be loaded. Close this section and try again.</p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirming === 'renew'}
        title="Renew this branch"
        message={
          'Renewing extends this branch only. Your other branches and your organisation '
          + 'subscription are unaffected.'
        }
        confirmLabel="Go to billing"
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          window.location.href = `/owner/listings/${branch.id}?tab=billing`;
        }}
      />
      <ConfirmDialog
        open={confirming === 'archive'}
        title="Archive this branch?"
        message="This branch will be removed from customer listings immediately. Its subscription and payment history will remain unchanged; the subscription period will continue to run and will not be refunded. The owner can restore the branch later."
        confirmLabel="Archive branch"
        destructive
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => void archive()}
      />
      <ConfirmDialog
        open={confirming === 'restore'}
        title="Unarchive this branch?"
        message="This restores the branch. If it was previously approved, it can return to customer listings without another Admin approval, provided its subscription and organisation access are valid. A branch awaiting its first approval remains hidden until it completes the normal first-time review."
        confirmLabel="Unarchive branch"
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => void restore()}
      />
      <ConfirmDialog
        open={confirming === 'delete'}
        title="Permanently delete this branch?"
        message="This permanently removes the branch and its inventory and cannot be undone. Branch staff will lose this branch assignment, but their accounts remain. Payment and subscription history is retained and detached from the branch, so it will appear as Deleted branch in billing history."
        confirmLabel="Delete permanently"
        destructive
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => void deletePermanently()}
      />
    </div>
  );
}

function BranchDetails({ detail }: { detail: PropertyDetail }) {
  const specificDetails: Array<[string, string]> = [];
  const add = (label: string, value: string | number | undefined | null) => {
    if (value !== undefined && value !== null && value !== '') specificDetails.push([label, String(value)]);
  };
  const addFlag = (label: string, value: boolean | undefined) => {
    if (value !== undefined) specificDetails.push([label, value ? 'Yes' : 'No']);
  };

  if (detail.pgDetail) {
    const pg = detail.pgDetail;
    add('Property type', humanise(pg.propertyType));
    add('Gender policy', humanise(pg.genderPolicy));
    add('Electricity', humanise(pg.electricityChargeType));
    add('Electricity fixed amount', pg.electricityFixedAmount);
    add('Maintenance charge', pg.maintenanceCharge);
    add('Notice period', pg.noticePeriodDays === undefined ? undefined : `${pg.noticePeriodDays} days`);
    add('Minimum stay', pg.minimumStayMonths === undefined ? undefined : `${pg.minimumStayMonths} months`);
    addFlag('Food provided', pg.foodProvided);
    addFlag('Breakfast included', pg.breakfastIncluded);
    addFlag('Lunch included', pg.lunchIncluded);
    addFlag('Dinner included', pg.dinnerIncluded);
    addFlag('Kitchen access', pg.kitchenAccess);
    addFlag('Vegetarian only', pg.isVegetarianOnly);
    addFlag('Curfew', pg.hasCurfew);
    add('Curfew time', pg.curfewTime);
    add('Visitor policy', pg.visitorPolicy);
    add('Check-in rules', pg.checkInRules);
    addFlag('Power backup', pg.hasPowerBackup);
    addFlag('Housekeeping', pg.hasHousekeeping);
    addFlag('Laundry service', pg.hasLaundry);
  } else if (detail.hotelDetail) {
    const hotel = detail.hotelDetail;
    add('Hotel type', humanise(hotel.hotelType));
    add('Star rating', hotel.starRating);
    add('Check-in', hotel.checkInTime);
    add('Check-out', hotel.checkOutTime);
    add('Reception hours', hotel.receptionHours);
    addFlag('24-hour reception', hotel.is24HourReception);
    addFlag('Pets allowed', hotel.petsAllowed);
    addFlag('Couples allowed', hotel.couplesAllowed);
    addFlag('Local ID accepted', hotel.localIdAccepted);
    addFlag('Restaurant', hotel.hasRestaurant);
    addFlag('Room service', hotel.hasRoomService);
    addFlag('Parking', hotel.hasParking);
    addFlag('Conference room', hotel.hasConferenceRoom);
    add('Cancellation policy', hotel.cancellationPolicy);
    add('Child policy', hotel.childPolicy);
  }

  if (detail.homeDetail) {
    const home = detail.homeDetail;
    add('Home type', humanise(home.homeType));
    add('Bedrooms', home.bedrooms);
    add('Bathrooms', home.bathrooms);
    add('Balconies', home.balconies);
    add('Furnishing', humanise(home.furnishing));
    add('Monthly rent', money(home.monthlyRent));
    add('Security deposit', money(home.securityDeposit));
    add('Car parking spaces', home.carParkingCount);
    add('Two-wheeler parking spaces', home.bikeParkingCount);
    add('Carpet area', home.carpetAreaSqft === undefined ? undefined : `${home.carpetAreaSqft} sq ft`);
    addFlag('Lift', home.hasLift);
    addFlag('Power backup', home.hasPowerBackup);
    addFlag('Rent negotiable', home.isRentNegotiable);
    add('Tenant preference notes', home.tenantPreferenceNotes);
    if (home.tenantPreferences.length) {
      add('Tenant preferences', home.tenantPreferences.map((preference) => preference.name).join(', '));
    }
  }

  return (
    <div>
      <h4 className="h6">Photos ({detail.photos.length})</h4>
      {detail.photos.length > 0 ? (
        <div className="d-flex gap-2 overflow-auto mb-3 pb-2" aria-label="Branch photos">
          {detail.photos.map((photo) => (
            <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer" aria-label={photo.altText || photo.caption || photo.category}>
              <img
                src={photo.thumbnailUrl ?? photo.url}
                alt={photo.altText || photo.caption || `${detail.title} - ${humanise(photo.category)}`}
                loading="lazy"
                style={{ width: 150, height: 100, objectFit: 'cover', borderRadius: 8 }}
              />
            </a>
          ))}
        </div>
      ) : (
        <p className="sf-muted small">No photos have been added.</p>
      )}

      <h4 className="h6">Facilities ({detail.amenities.length})</h4>
      {detail.amenities.length > 0 ? (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {detail.amenities.map((amenity) => (
            <span className="sf-chip" key={amenity.id}>{amenity.name}</span>
          ))}
        </div>
      ) : (
        <p className="sf-muted small">No facilities selected yet.</p>
      )}

      {specificDetails.length > 0 && (
        <>
          <h4 className="h6">Property details</h4>
          <dl className="row mb-0 small">
            {specificDetails.map(([label, value]) => (
              <div className="col-12 col-md-6 d-flex gap-2 mb-1" key={label}>
                <dt className="sf-muted fw-normal">{label}:</dt>
                <dd className="mb-0">{value}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {detail.roomTypes.length > 0 && (
        <>
          <h4 className="h6 mt-3">Rooms &amp; sharing options</h4>
          <ul className="small mb-0">
            {detail.roomTypes.map((roomType) => (
              <li key={roomType.id}>
                {roomType.name}
                {roomType.sharingType && ` · ${humanise(roomType.sharingType)}`}
                {roomType.monthlyRent !== undefined && ` · ${money(roomType.monthlyRent)} per month`}
                {roomType.pricePerNight !== undefined && ` · ${money(roomType.pricePerNight)} per night`}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
